import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'grommet';
import { Box, Input, Select, Text } from '/src/components';
import { TUserId, User } from '/src/lib/models';

export const ContactsSelect: React.FC<ContactsSelectProps> = (props) => {
  const { id, contacts = [], isUpdatePage, isCorporate, isRemittance, isFacility } = props;
  const { t } = useTranslation();

  const [selectedContactId, setSelectedContactId] = useState<TUserId>();

  // const contacts = useMemo(() => (users ?? []).filter((user) => user.role.id === RoleTypes.ClientAdmin), [users]);
  const entityName = useMemo(
    () => (isCorporate || isRemittance ? t('contacts:client') : isFacility ? t('common:facility') : undefined),
    [isCorporate, isRemittance, isFacility, t]
  );
  const contactTypeName = useMemo(
    () =>
      isCorporate
        ? t('contacts:corporateContacts')
        : isRemittance
          ? t('contacts:remittanceContacts')
          : isFacility
            ? t('contacts:facilityContacts')
            : undefined,
    [isCorporate, isRemittance, isFacility, t]
  );
  const selectedContact = useMemo(
    () => contacts.find((contact) => contact.id === selectedContactId) ?? contacts[0],
    [contacts, selectedContactId]
  );

  const formatPhoneNumber = (value: string) => {
    const digitsOnly = value.replace(/[^\d]/g, '');

    if (!digitsOnly) return '';

    if (digitsOnly.length >= 10) {
      return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6, 10)}`;
    }
    return value;
  };

  useEffect(() => {
    if (!!contacts?.length && !selectedContactId) {
      setSelectedContactId(contacts[0]?.id);
    }
  }, [contacts, selectedContactId]);

  return (
    <Box gap="1rem" fill="horizontal">
      {!isUpdatePage && (
        <Box
          id={id}
          pad="1rem"
          gap="0.5rem"
          background="light-6"
          border={{ size: 'small', color: 'light-5' }}
          borderRadius="6px"
          fontFamily="Lato, sans-serif"
        >
          <Text size="large">{t('contacts:pointsOfContact')}</Text>
          <Text size="medium">{t('contacts:pointsOfContactDescription', { entityType: entityName })}</Text>
        </Box>
      )}
      {isUpdatePage && (
        <Box id={id} gap="1rem">
          <Text color="dark-1" size="large" weight={300}>
            {contactTypeName}
          </Text>
          <Select
            name="contact_name"
            label={t('contacts:contactName')}
            placeholder={t('contacts:choose')}
            value={selectedContactId}
            setValue={(value) => setSelectedContactId(value)}
            options={contacts.map((user) => ({ label: user.name, value: user.id }))}
            hideOptionalText
          />
          <Box height="8rem">
            {selectedContact && (
              <Box gap="1rem">
                <Button plain>
                  <Input
                    name="contact_email"
                    label={t('contacts:contactEmail')}
                    value={selectedContact.email}
                    placeholder=""
                    // eslint-disable-next-line @typescript-eslint/no-empty-function
                    setValue={() => {}}
                    hideOptionalText
                    disabled
                  />
                </Button>
                <Input
                  name="contact_phone"
                  label={t('contacts:contactPhone')}
                  value={selectedContact.phone ? formatPhoneNumber(selectedContact.phone) : ''}
                  placeholder=""
                  // eslint-disable-next-line @typescript-eslint/no-empty-function
                  setValue={() => {}}
                  hideOptionalText
                  flex="grow"
                  disabled
                />
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export type ContactsSelectProps = {
  id?: string;
  contacts?: User[];
  isUpdatePage?: boolean;
  isClient?: boolean;
  isCorporate?: boolean;
  isRemittance?: boolean;
  isFacility?: boolean;
};
